import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  clearAuthStorage,
  getStoredToken,
  getStoredUserId,
  isLikelyJwtToken,
} from '@/utils/auth'
const API_PREFIX = '/api'

const shouldAttachUserIdToBody = (url?: string): boolean => {
  if (!url) return false
  return (
    url.startsWith('/api/v1/asin') ||
    url.startsWith('/api/v1/monitor-rule') ||
    url.startsWith('/v1/asin') ||
    url.startsWith('/v1/monitor-rule')
  )
}

const normalizeRequestUrl = (url?: string): string | undefined => {
  if (!url) return url

  const baseURL = import.meta.env.VITE_API_BASE_URL || API_PREFIX
  const normalizedBaseURL = baseURL.replace(/\/+$/, '')
  const normalizedUrl = url.trim()

  if (normalizedBaseURL.endsWith(API_PREFIX) && normalizedUrl.startsWith(`${API_PREFIX}/`)) {
    return normalizedUrl.substring(API_PREFIX.length)
  }

  return normalizedUrl
}

// 创建axios实例
const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || API_PREFIX,
  timeout: 30000,
  paramsSerializer: {
    serialize: (params) => {
      // 使用 URLSearchParams 手动处理数组参数
      // Spring Boot @RequestParam List<Integer> 期望 sids=1&sids=2 格式（repeat 模式，不带方括号索引）
      const usp = new URLSearchParams()
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined) continue
        if (Array.isArray(value)) {
          value.forEach((v) => {
            if (v !== null && v !== undefined) {
              usp.append(key, String(v))
            }
          })
        } else {
          usp.append(key, String(value))
        }
      }
      return usp.toString()
    },
  },
})

// 请求队列（用于取消重复请求）
const pendingMap = new Map<string, AbortController>()

// 生成请求key
const getRequestKey = (config: AxiosRequestConfig | InternalAxiosRequestConfig): string => {
  let dataStr: string
  if (config.data instanceof FormData) {
    dataStr = `FormData_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  } else {
    dataStr = JSON.stringify(config.data)
  }
  return `${config.method}_${config.url}_${JSON.stringify(config.params)}_${dataStr}`
}

// 添加请求到队列
const addPending = (config: AxiosRequestConfig | InternalAxiosRequestConfig): void => {
  const key = getRequestKey(config)
  const controller = new AbortController()
  config.signal = controller.signal
  pendingMap.set(key, controller)
}

// 移除请求从队列
const removePending = (
  config: AxiosRequestConfig | InternalAxiosRequestConfig,
  cancelRequest = true,
): void => {
  const key = getRequestKey(config)
  if (pendingMap.has(key)) {
    const controller = pendingMap.get(key)
    if (cancelRequest) {
      const method = (config.method || 'get').toLowerCase()
      // GET 请求是幂等的，不取消重复请求，避免 ERR_ABORTED
      if (method !== 'get') {
        controller?.abort()
      }
    }
    pendingMap.delete(key)
  }
}

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.url = normalizeRequestUrl(config.url)

    // 移除重复请求
    removePending(config)
    addPending(config)

    // 添加token
    const token = getStoredToken()
    config.headers = config.headers || {}
    if (config.data instanceof FormData) {
      const headers = config.headers as any
      headers['Content-Type'] = undefined
      headers['content-type'] = undefined
    } else {
      const isPlainObject =
        !!config.data && typeof config.data === 'object' && !(config.data instanceof FormData)
      if (isPlainObject) {
        const headers = config.headers as any
        if (!headers['Content-Type'] && !headers['content-type']) {
          headers['Content-Type'] = 'application/json'
        }
      }
    }
    if (token && isLikelyJwtToken(token)) {
      (config.headers as any).Authorization = `Bearer ${token}`
    } else if (token) {
      clearAuthStorage()
    }

    const userId = getStoredUserId()
    if (userId) {
      (config.headers as any)['X-User-Id'] = userId
    }

    const method = (config.method || '').toLowerCase()
    const canAttachToBody = method === 'post' || method === 'put' || method === 'patch'
    if (canAttachToBody && userId && shouldAttachUserIdToBody(config.url)) {
      const isPlainObject =
        !!config.data && typeof config.data === 'object' && !(config.data instanceof FormData)
      if (isPlainObject && !(config.data as any).userId) {
        config.data = { ...(config.data as any), userId }
      }
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 移除请求
    removePending(response.config, false)

    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response.data
    }

    const { code, message, data } = response.data

    // 成功响应
    if (code === 200 || code === 0) {
      return data
    }

    // 业务错误
    if (!(response.config as any).silent) {
      ElMessage.error(message || '请求失败')
    }
    if (typeof message === 'string' && message.includes('用户ID不能为空')) {
      clearAuthStorage()
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(new Error(message || '请求失败'))
  },
  (error: AxiosError) => {
    // 移除请求
    if (error.config) {
      removePending(error.config, false)
    }

    // 处理错误
    if (error.response) {
      const { status, data } = error.response as AxiosResponse

      switch (status) {
        case 401:
          ElMessageBox.confirm('登录已过期，请重新登录', '提示', {
            confirmButtonText: '重新登录',
            cancelButtonText: '取消',
            type: 'warning',
          }).then(() => {
            clearAuthStorage()
            window.location.href = '/login'
          })
          break
        case 403:
          ElMessage.error('没有权限执行此操作')
          break
        case 404:
          ElMessage.error('请求的资源不存在')
          break
        case 500:
          ElMessage.error('服务器内部错误')
          break
        default:
          ElMessage.error((data as any)?.message || `请求失败: ${status}`)
      }
    } else if (error.request) {
      ElMessage.error('网络请求失败，请检查网络连接')
    } else {
      ElMessage.error(error.message || '请求失败')
    }

    return Promise.reject(error)
  }
)

// 封装GET请求
export function get<T = any>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> {
  return request.get(url, { params, ...config })
}

// 封装POST请求
export function post<T = any>(url: string, data?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> {
  return request.post(url, data, config)
}

// 封装PUT请求
export function put<T = any>(url: string, data?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> {
  return request.put(url, data, config)
}

// 封装DELETE请求
export function del<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  return request.delete(url, { data, ...config })
}

// 封装PATCH请求
export function patch<T = any>(url: string, data?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> {
  return request.patch(url, data, config)
}

// 封装上传请求
export function upload<T = any>(
  url: string,
  file: File,
  fields?: Record<string, string | number | null | undefined>,
  onProgress?: (progress: number) => void,
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  if (fields) {
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value))
      }
    })
  }

  return request.post(url, formData, {
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  })
}

// 封装下载请求
export function download(url: string, params?: Record<string, any>, filename?: string): Promise<void> {
  return get<BlobPart>(url, params, {
    responseType: 'blob',
  }).then((blobPart) => {
    const blob = new Blob([blobPart])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  })
}

export default request
